import { Modal } from 'react-daisyui';
import { Button } from '@leadsales/design-system/react';
import { useTranslation } from 'react-i18next';

/**
 * Confirmación de una SEGUNDA recarga del mismo monto dentro de la ventana de
 * acreditación (RET-1925, criterio 5).
 *
 * EL DEFECTO QUE CIERRA, y no es una cortesía: mientras el saldo se acredita la
 * tarjeta se veía igual que en reposo, así que quien creía que su recarga no
 * entró la repetía. Y la segunda **cobra de verdad** — el modal descarta la
 * llave del intento al recibir un 200 definitivo, y la llave de idempotencia se
 * compone con ese identificador, así que una segunda recarga produce llave nueva
 * y Stripe la trata como cobro distinto.
 *
 * Ese descarte es CORRECTO POR DISEÑO y no se toca: es lo que permite recargar
 * el mismo monto dos veces el mismo día a propósito. Por eso el candado es de
 * INTERFAZ y no de llave. Lo que se agrega es fricción, no un bloqueo: un
 * candado que no se puede pasar es peor que el bug, porque convierte una
 * intención legítima en un callejón.
 *
 * Las tres frases que decide el copy, y por qué cada una:
 *
 *   - **ya se cobró**: es el hecho que el cliente no tiene en pantalla y por el
 *     que está a punto de pagar dos veces.
 *   - **se hace un cobro NUEVO**: nombra la consecuencia en el momento de
 *     decidir, no después. Y dice que no se reembolsa desde aquí, porque no se
 *     puede (P16 / RET-1727) y descubrirlo después es peor.
 *   - **esperar**: la salida por defecto es no cobrar. El botón de cancelar
 *     nombra lo que pasa si se cancela —la acreditación sigue viniendo— en vez
 *     de decir sólo "cancelar", que no dice qué se cancela.
 */
export default function RepeatTopUpModal(props: {
  isOpen: boolean;
  isBusy?: boolean;
  /** El monto que ya se cobró, ya formateado por el conectado. */
  chargedAmountLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const {
    isOpen,
    isBusy = false,
    chargedAmountLabel,
    onCancel,
    onConfirm,
  } = props;
  const { t } = useTranslation('wallet');

  // No se monta cerrado, por la misma razón que `ReplaceRecurringModal`:
  // `Modal.Legacy` deja su contenido en el DOM aunque `open` sea false, y para
  // una CONFIRMACIÓN eso no es cosmético — el texto y sus botones quedan
  // alcanzables por un lector de pantalla y por cualquier consulta que no filtre
  // visibilidad.
  if (!isOpen) return null;

  return (
    <Modal.Legacy
      open={isOpen}
      onClickBackdrop={onCancel}
      className="RepeatTopUpModal rounded-lg"
    >
      <div data-testid="wallet-repeat-top-up-modal">
        <h3>{t('topUp.repeat.confirmTitle')}</h3>

        <p>{t('topUp.repeat.confirmBody', { amount: chargedAmountLabel })}</p>

        <p>{t('topUp.repeat.confirmNewCharge')}</p>

        <div className="modal-action">
          <Button
            color="ghost"
            size="md"
            onClick={onCancel}
            disabled={isBusy}
            data-testid="wallet-repeat-top-up-cancel"
          >
            {t('topUp.repeat.confirmCancel')}
          </Button>
          <Button
            color="primary"
            size="md"
            loading={isBusy}
            disabled={isBusy}
            onClick={onConfirm}
            data-testid="wallet-repeat-top-up-confirm"
          >
            {t('topUp.repeat.confirmAccept')}
          </Button>
        </div>
      </div>
    </Modal.Legacy>
  );
}
